import request from "../core/request";
import safeCall from "../core/safeCall";
import validate from "../core/validate";
import { shuffle, trim, orderBy } from "lodash";

/**
 * list all student homeworks
 *
 *
 * @returns {Promise<*>}
 */
export const getHomeworks = async (payload) => {
  validate(
    {
      page: {
        presence: {
          allowEmpty: false,
        },
        numericality: {
          onlyInteger: true,
        },
      },
      size: {
        presence: {
          allowEmpty: false,
        },
        numericality: {
          onlyInteger: true,
        },
      },
    },
    payload
  );

  return safeCall(
    async () => {
      return await request({
        url: "homeworks",
        method: "get",
        version: "v2",
        params: {
          page: payload.page,
          size: payload.size,
        },
      });
    },
    "error_get_homeworks_list",
    "Unexpected error to list Homeworks."
  );
};

/**
 * get student homeworks count
 *
 *
 * @returns {Promise<*>}
 */
export const getHomeworksCount = async () => {
  return safeCall(
    async () => {
      return await request({
        url: "homeworks/count",
        method: "get",
        version: "v2",
      });
    },
    "error_getting_homeworks_count",
    "Unexpected error to get Homeworks count."
  );
};

/**
 * get student homeworks count
 *
 *
 * @returns {Promise<{ total: number; completed: number}>}
 */
export const getHomeworksStats = async () => {
  return safeCall(
    async () => {
      return await request({
        url: "homeworks/stats",
        method: "get",
        version: "v2",
      });
    },
    "error_getting_homeworks_stats",
    "Unexpected error to get Homeworks stats."
  );
};

/**
 * start the execution of a homework
 *
 * @param {Object} payload - The object with all the params
 * @param {string} payload.module - the module id
 * @param {string} payload.unit - the unit id
 * @param {string} payload.homework - the unit id
 *
 * @throws {ApiError} throws an exception with the api error
 *
 * @returns {Promise<*>}
 */
export const startHomeworkExecution = async (payload) => {
  validate(
    {
      homework: {
        presence: {
          allowEmpty: false,
        },
      },
      unit: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    payload
  );

  return await safeCall(
    async () => {
      const result = await request({
        url: `homeworks/${payload.homework}/units/${payload.unit}/executions`,
        method: "post",
        version: "v2",
      });

      return {
        unitExecution: result.unitExecution,
        items: orderBy(
          result.items.map((unitItem) => ({
            ...unitItem,
            item: {
              ...unitItem.item,
              text: ["TEXT", "UNSCRAMBLE_TEXT"].some((type) => type === unitItem.item.type.key)
                ? trim(unitItem.item.text)
                : trim(unitItem.item.text).replace(/\s+/g, " "),
              answers: shuffle(unitItem.item.answers),
            },
          })),
          "order",
          "asc"
        ),
      };
    },
    "error_start_homework_execution",
    "Unexpected error to start homework execution."
  );
};

/**
 * end the execution of an unit and get the score
 *
 * @param {!Object} payload - The object with all the params
 * @param {!string} payload.module - the module id
 * @param {!string} payload.homework - the homework id
 * @param {!string} payload.unit - the unit id
 * @param {!string} payload.unitExecution - the unitResult id
 *
 * @throws {ApiError} throws an exception with the api error
 *
 * @returns {Promise<*>}
 */
export const endHomeworkExecution = async (payload) => {
  validate(
    {
      unit: {
        presence: {
          allowEmpty: false,
        },
      },
      unitExecution: {
        presence: {
          allowEmpty: false,
        },
      },
      homework: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    payload
  );

  return await safeCall(
    async () => {
      return await request({
        url: `homeworks/${payload.homework}/units/${payload.unit}/executions/${payload.unitExecution}`,
        method: "put",
        version: "v2",
      });
    },
    "error_end_homework_execution",
    "Unexpected error to end homework execution."
  );
};
